import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import type { AdminCustomerResponse, CustomerResponse, UpdateCustomerRequest } from "./customersApi";
import { deleteCustomer, updateCustomer } from "./customersApi";
import { useCustomerWithId } from "./useCustomerWithId";

export const useCustomer = () => {
  const { customerId } = useParams<{ customerId: string }>();
  if (!customerId) {
    throw new Error("useCustomer requires a customerId route param");
  }
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { customer, isLoading } = useCustomerWithId(customerId);
  // EditCustomerPage, this hook's only consumer, renders under /customers (ADMIN/AGENT only) — see
  // AdminCustomerResponse in customersApi.ts for why that lets us narrow the role-trimmed fields.
  const adminCustomer = customer as AdminCustomerResponse | undefined;

  const updateCustomerMutation = useMutation<
    CustomerResponse,
    unknown,
    { customerId: string; update: UpdateCustomerRequest }
  >({
    mutationFn: ({ customerId, update }) => updateCustomer(customerId, update),
    onSuccess: async (data) => {
      enqueueSnackbar(`Klient ${data.name} został zaktualizowany`, { variant: "success" });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customers", customerId] }),
        queryClient.invalidateQueries({ queryKey: ["customers"] }),
        queryClient.invalidateQueries({ queryKey: ["customers", customerId, "notes"] }),
      ]);
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować klienta. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const deleteCustomerMutation = useMutation<void, unknown, { customerId: string }>({
    mutationFn: ({ customerId }) => deleteCustomer(customerId),
    onSuccess: async () => {
      enqueueSnackbar("Klient został usunięty", { variant: "success" });
      queryClient.removeQueries({ queryKey: ["customers", customerId] });
      queryClient.removeQueries({ queryKey: ["customers", customerId, "notes"] });
      await queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć klienta. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  return {
    customer: adminCustomer,
    isLoading,
    updateCustomer: (value: UpdateCustomerRequest) =>
      updateCustomerMutation.mutateAsync({ customerId: customerId, update: value }),
    deleteCustomer: () => deleteCustomerMutation.mutateAsync({ customerId }),
  };
};
